/**
 * English catalog — connecting an AI to the workspace without a key.
 *
 * Two surfaces share these strings: the /connect page (where a person types
 * the code a device showed them — RFC 8628's human half) and the keyless
 * sections of the developer docs. Split from en.ts like enAssistant, keeping
 * every catalog file under the 500-line fileSize ratchet.
 */
export const enConnect = {
  // ── /connect — code entry ─────────────────────────────────────────────
  'connect.title': 'Connect a device',
  'connect.subtitle':
    'A terminal or AI app showed you a short code. Enter it here to connect it to your workspace.',
  'connect.codeLabel': 'Code',
  'connect.codePlaceholder': 'XXXX-XXXX',
  'connect.continue': 'Continue',
  'connect.checking': 'Checking…',
  'connect.notFound': 'That code was not recognized. Check it and try again.',
  'connect.expired': 'That code has expired. Start again on the device and enter the new code.',
  'connect.alreadyDecided': 'That code has already been used.',

  // ── /connect — approval ───────────────────────────────────────────────
  'connect.approve.heading': 'Connect {name}',
  'connect.approve.subtitle': '{name} is asking to connect to your BuzzBit X workspace.',
  'connect.approve.workspace': 'Workspace',
  'connect.approve.permissions': 'It may',
  'connect.approve.approve': 'Connect',
  'connect.approve.approving': 'Connecting…',
  'connect.approve.deny': 'Deny',
  'connect.approve.noWorkspaces':
    'You have no workspace yet. Create one before connecting devices.',
  'connect.approve.gone':
    'This code is no longer waiting — it may have expired or been decided elsewhere.',
  'connect.approve.revokeNote':
    'You can disconnect it any time under Settings → Connected apps.',

  // Scope lines mirror the consent screen's plain language.
  'connect.scope.read': 'Read your business data — metrics, customers, bookings, flows and campaigns.',
  'connect.scope.write': 'Create drafts. Anything it writes stays turned off until you enable it yourself.',
  'connect.scope.execute':
    'Send a single message, and ask to send to many. You confirm every bulk send yourself.',
  'connect.scope.admin': 'Manage workspace settings. It still cannot spend money — nothing can.',

  // ── /connect — done ───────────────────────────────────────────────────
  'connect.done.approved': 'Connected. You can return to the device — it signs itself in now.',
  'connect.done.denied': 'Denied. The device was not connected.',
  'connect.done.close': 'You can close this page.',
  'connect.done.manage': 'See what is connected',

  // ── Settings → Connected apps ─────────────────────────────────────────
  // The page the /connect copy and the bridge's own messages send people to.
  'connectedApps.title': 'Connected apps',
  'connectedApps.subtitle':
    'AI apps connected to this workspace — revoke anytime.',
  'connectedApps.loading': 'Loading…',
  'connectedApps.loadFailed': 'The list could not be loaded. Try again in a moment.',
  'connectedApps.empty':
    'Nothing is connected. An app you sign in from a terminal, or connect in the browser, will be listed here.',
  'connectedApps.how.device': 'Signed in from a terminal',
  'connectedApps.how.browser': 'Connected in the browser',
  'connectedApps.how.apiKey': 'An API key',
  'connectedApps.connectedOn': 'Connected {when}',
  'connectedApps.lastUsed': 'Last used {when}',
  'connectedApps.neverUsed': 'Never used',
  'connectedApps.revoke': 'Revoke',
  'connectedApps.revoke.confirm':
    'Revoke {name}? It loses access at once and has to be connected again to come back.',
  'connectedApps.revoke.yes': 'Yes, revoke',
  'connectedApps.revoke.no': 'Keep it',
  'connectedApps.revoke.busy': 'Revoking…',
  'connectedApps.revoke.failed': 'That connection could not be revoked. Try again in a moment.',
  'connectedApps.revoked': '{name} no longer has access.',
  'connectedApps.link': 'Connected apps',
  'connectedApps.fromAssistant':
    'An AI app signed in from a terminal or connected in the browser is listed under Connected apps, where any of them can be revoked.',

  // ── Docs — the keyless path ───────────────────────────────────────────
  'docs.device.title': 'Sign in from your AI tool (no key)',
  'docs.device.claudeCodeHeading': 'Claude Code',
  'docs.device.localHeading': 'LM Studio, Jan, Open WebUI',
  'docs.device.localBody':
    'These apps only run MCP servers as a local program, so they cannot connect to the endpoint directly.',
  'docs.device.webHeading': 'On the web',
  'docs.device.webBody':
    'Claude on the web (claude.ai → Settings → Connectors) and ChatGPT (Settings → Connectors, developer mode) connect as remote connectors: paste the endpoint below, sign in when asked, and consent in the browser. No install, no snippet, no key anywhere.',
  'docs.device.ollamaHeading': 'Ollama and other local models',
  'docs.device.ollamaBody':
    'A model on your laptop connects OUTWARD: the host running it calls BuzzBit X, so localhost never needs to be reachable from the internet. (This is the opposite direction from bringing your own inference endpoint for the in-app assistant — that endpoint must be HTTPS and reachable from the internet, which a laptop usually is not.)',
  'docs.device.keyNote':
    'API keys still work and are not deprecated — they are the right tool for headless servers and CI, where no browser exists to sign in with.',
  // Marketing-approved wording, 2026-10-03: no install command on the docs pages.
  'docs.claudeCode.connect': 'You can connect Claude Code to BuzzBit X with the plugin.',
  'docs.hero.intro':
    'BuzzBit X exposes 100+ tools over the Model Context Protocol.',
  'docs.overview.lead': 'BuzzBit X speaks the Model Context Protocol at',
  'docs.overview.body':
    'MCP clients sign in with your BuzzBit X account: no key is created, shown or pasted. A bz_live_… API key is for scripts, servers and the bbx CLI.',
  'docs.quickstart.signIn':
    'Sign in with your BuzzBit X account in the browser, choose the workspace and approve.',
  'docs.quickstart.ask': 'Ask it something like "how many orders did I get last week?"',
  'docs.desktop.steps':
    'In Claude Desktop: Settings → Connectors → Add custom connector. Paste this address, then sign in with your BuzzBit X account when asked. There is no config file to edit and no key.',

  // ── ChatGPT guide (/docs/chatgpt) + Custom MCP setup modal ───────────
  'docs.chatgpt.title': 'Connect ChatGPT to BuzzBit X',
  'docs.chatgpt.intro':
    'Add BuzzBit X as a remote MCP connector in ChatGPT. ChatGPT signs in with your BuzzBit X account in the browser — no API key to paste.',
  'docs.chatgpt.nav': 'Connect ChatGPT',
  'docs.chatgpt.overview.title': 'Overview',
  'docs.chatgpt.overview.body':
    'BuzzBit X exposes 100+ tools over the Model Context Protocol at the HTTPS Streamable HTTP endpoint below. ChatGPT connects as a custom MCP server (developer mode / Plugins). Do not open the URL in a normal browser tab — ChatGPT must POST to it.',
  'docs.chatgpt.find.title': 'Where to add a custom MCP server',
  'docs.chatgpt.find.plugins':
    'On ChatGPT web: open Plugins, select the plus (+) button, then Add custom MCP server (some workspaces label this as creating a developer mode app).',
  'docs.chatgpt.find.connectors':
    'Business / Enterprise workspaces may also manage custom MCP apps from workspace Plugins settings. Account and workspace policies still apply.',
  'docs.chatgpt.devMode.title': 'Enable Developer Mode (if required)',
  'docs.chatgpt.devMode.body':
    'If you cannot add a custom MCP server, open ChatGPT Settings → Security and login and turn on Developer Mode, then return to Plugins and use the plus button again. Plus and higher plans on the web support this; mobile apps do not.',
  'docs.chatgpt.add.title': 'Add the BuzzBit X MCP endpoint',
  'docs.chatgpt.add.body':
    'Enter a name, optionally a description, then under Connection paste this Server URL exactly (include /mcp). Choose OAuth for authentication. Review the risk warning, confirm you understand, and create the plugin / app. ChatGPT uses Streamable HTTP POST — not a browser GET of this URL.',
  'docs.chatgpt.oauth.title': 'Complete OAuth authorization',
  'docs.chatgpt.oauth.body':
    'When ChatGPT opens BuzzBit X, sign in, choose your workspace, and approve. ChatGPT registers itself (Dynamic Client Registration) and keeps the token — you never copy a key. Finish the sign-in in the same browser window.',
  'docs.chatgpt.verify.title': 'Verify the connection and use tools',
  'docs.chatgpt.verify.body':
    'Install or enable the resulting plugin if asked. Start a new chat, open the plus menu (or type @), choose Developer mode / your BuzzBit X app, and ask something like "how many orders did I get last week?". Successful tool calls mean it is live. In BuzzBit X, Settings → Connected apps should list ChatGPT.',
  'docs.chatgpt.disconnect.title': 'Disconnect and reconnect',
  'docs.chatgpt.disconnect.body':
    'In BuzzBit X: Settings → Connected apps → Revoke on the ChatGPT row. In ChatGPT: remove or disable the custom MCP app from Plugins / app settings, then add the Server URL again and complete OAuth to reconnect.',
  'docs.chatgpt.endpointLabel': 'MCP server URL',
  'docs.chatgpt.openSettings': 'Open Settings',
  'docs.chatgpt.helpPrefix': 'Need help?',
  'docs.chatgpt.supportEmail': 'support@buzzbitx.com',
  'ai.setup.chatgpt.steps': 'Setup in ChatGPT',
  'ai.setup.chatgpt.step1':
    'On ChatGPT web: Settings → Security and login → turn on Developer Mode if you cannot add custom MCP servers. Then open Plugins → plus (+) → Add custom MCP server.',
  'ai.setup.chatgpt.step2':
    'Paste the MCP server URL below as the Server URL (Streamable HTTP). Choose OAuth. Do not open this URL in a normal browser tab.',
  'ai.setup.chatgpt.step3':
    'When ChatGPT opens BuzzBit X, sign in, choose your workspace, and approve. No API key is created or pasted.',
  'ai.setup.chatgpt.step4':
    'In a new chat, open plus → Developer mode (or @ your app) and ask a business question. Confirm ChatGPT appears under Settings → Connected apps.',
  'ai.setup.chatgpt.manageAccess': 'Manage ChatGPT access',

  // ── Email verification banner ─────────────────────────────────────────
  'emailVerification.banner': 'Please verify your email address. Check your inbox for a verification link.',
  'emailVerification.resend': 'Resend Email',
  'emailVerification.sending': 'Sending...',
  'emailVerification.sent': 'Sent!',
  'emailVerification.dismiss': 'Dismiss',
  'emailVerification.resendFailed': 'We could not send the email. Please try again in a minute.',
} as const;
